import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Building, TrendingUp, Target, AlertCircle, RefreshCw, CheckCircle, Edit, Save, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IndustryProfilePanel = ({ companyId, userToken, onReconfigure }) => {
  // Get token from localStorage if not provided
  const token = userToken || localStorage.getItem('token');
  const queryClient = useQueryClient();
  
  const [isEditingIndustry, setIsEditingIndustry] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedBusinessModel, setSelectedBusinessModel] = useState('');
  const [selectedOperationalStructure, setSelectedOperationalStructure] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  const { data: masterContext, isLoading, refetch, error } = useQuery({
    queryKey: ['master-context', companyId],
    queryFn: async () => {
      console.log('[IndustryProfilePanel] Fetching master context for company:', companyId);
      const response = await axios.get(`${API}/companies/${companyId}/master-context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[IndustryProfilePanel] Response:', response.data);
      return response.data;
    },
    enabled: !!companyId && !!token,
    staleTime: 0, // Always refetch
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2
  });
  
  // Fetch available industries from taxonomy
  const { data: industriesData } = useQuery({
    queryKey: ['available-industries'],
    queryFn: async () => {
      const response = await axios.get(`${API}/industries/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });
  
  const industries = industriesData?.industries || [];
  
  // Get data for selected primary industry
  const currentIndustryData = industries.find(i => i.name === selectedIndustry);
  const businessModels = currentIndustryData?.business_models || [];
  // Operational structures are stored in secondary_industries
  const operationalStructures = currentIndustryData?.secondary_industries || [];
  // Categories are in sub_industries based on selected operational structure
  const subIndustriesMap = currentIndustryData?.sub_industries || {};
  const categories = selectedOperationalStructure ? (subIndustriesMap[selectedOperationalStructure] || []) : [];
  
  // Update industry mutation
  const updateIndustryMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.put(
        `${API}/companies/${companyId}/industry`,
        {
          industry: selectedIndustry,
          business_model: selectedBusinessModel,
          operational_structure: selectedOperationalStructure,
          category: selectedCategory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Industria actualizada correctamente');
      setIsEditingIndustry(false);
      queryClient.invalidateQueries(['master-context', companyId]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar industria');
    }
  });
  
  // Log any errors
  React.useEffect(() => {
    if (error) {
      console.error('[IndustryProfilePanel] Error fetching master context:', error);
    }
  }, [error]);
  
  // Initialize selected values when editing starts
  const handleStartEdit = () => {
    const classification = masterContext?.classification || {};
    setSelectedIndustry(classification.primary_industry || '');
    setSelectedBusinessModel(classification.business_model || '');
    setSelectedOperationalStructure(classification.operational_structure || classification.secondary_industry || '');
    setSelectedCategory(classification.category || '');
    setIsEditingIndustry(true);
  };
  
  const handleSaveIndustry = () => {
    if (!selectedIndustry) {
      toast.error('Selecciona una industria');
      return;
    }
    updateIndustryMutation.mutate();
  };

  if (isLoading) {
    return (
      <Card className="glass-dark border-blue-500/20">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-slate-400 mt-4">Cargando perfil de industria...</p>
        </CardContent>
      </Card>
    );
  }

  if (!masterContext) {
    return (
      <Card className="glass-dark border-yellow-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-400" />
            Perfil de Industria No Configurado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 mb-4">
            No has configurado el perfil de tu empresa. Esto es necesario para obtener análisis personalizados.
          </p>
          <Button onClick={onReconfigure} className="btn-gradient">
            <Building className="w-4 h-4 mr-2" />
            Configurar Ahora
          </Button>
        </CardContent>
      </Card>
    );
  }

  const classification = masterContext.classification || {};
  const dataQuality = masterContext.data_quality || {};
  const confidenceScore = masterContext.confidence_score || 0;

  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getConfidenceBg = (score) => {
    if (score >= 80) return 'bg-green-500/20 border-green-500/30';
    if (score >= 60) return 'bg-yellow-500/20 border-yellow-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  return (
    <div className="space-y-6">
      {/* Main Classification Card */}
      <Card className="glass-dark border-blue-500/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Building className="w-5 h-5 text-blue-400" />
              Perfil de Industria
            </CardTitle>
            <div className="flex items-center gap-2">
              {!isEditingIndustry && (
                <Button
                  onClick={handleStartEdit}
                  variant="ghost"
                  size="sm"
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Cambiar Industria
                </Button>
              )}
              <Button
                onClick={() => refetch()}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Industry Selection Mode */}
          {isEditingIndustry && (
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg space-y-4">
              <p className="text-sm text-blue-300 font-medium">Configura tu perfil de industria</p>
              <div className="grid grid-cols-2 gap-4">
                {/* Industria Principal */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Industria *</label>
                  <Select value={selectedIndustry} onValueChange={(v) => {
                    setSelectedIndustry(v);
                    setSelectedBusinessModel('');
                    setSelectedOperationalStructure('');
                    setSelectedCategory('');
                  }}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Seleccionar industria" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {industries.map((ind) => (
                        <SelectItem key={ind.id} value={ind.name}>{ind.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Modelo de Negocio */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Modelo de Negocio</label>
                  <Select 
                    value={selectedBusinessModel} 
                    onValueChange={setSelectedBusinessModel}
                    disabled={!selectedIndustry || businessModels.length === 0}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder={businessModels.length === 0 ? "Sin modelos disponibles" : "Seleccionar modelo"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {businessModels.map((model, idx) => (
                        <SelectItem key={idx} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Estructura Operativa */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Estructura Operativa</label>
                  <Select 
                    value={selectedOperationalStructure} 
                    onValueChange={(v) => {
                      setSelectedOperationalStructure(v);
                      setSelectedCategory('');
                    }}
                    disabled={!selectedIndustry || operationalStructures.length === 0}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder={operationalStructures.length === 0 ? "Sin estructuras disponibles" : "Seleccionar estructura"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {operationalStructures.map((struct, idx) => (
                        <SelectItem key={idx} value={struct}>{struct}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Categoría */}
                <div>
                  <label className="text-xs text-slate-400 mb-2 block">Categoría</label>
                  <Select 
                    value={selectedCategory} 
                    onValueChange={setSelectedCategory}
                    disabled={!selectedOperationalStructure || categories.length === 0}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder={categories.length === 0 ? "Selecciona estructura primero" : "Seleccionar categoría"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {categories.map((cat, idx) => (
                        <SelectItem key={idx} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingIndustry(false)}
                  className="text-slate-400"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveIndustry}
                  disabled={updateIndustryMutation.isPending || !selectedIndustry}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {updateIndustryMutation.isPending ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          )}

          {/* Classification Display - 4 campos */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 mb-2">Industria</p>
              <p className="text-lg font-semibold text-white">
                {classification.primary_industry || 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 mb-2">Modelo de Negocio</p>
              <p className="text-lg font-semibold text-white">
                {classification.business_model || 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 mb-2">Estructura Operativa</p>
              <p className="text-lg font-semibold text-white">
                {classification.operational_structure || classification.secondary_industry || 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 mb-2">Categoría</p>
              <p className="text-lg font-semibold text-white">
                {classification.category || 'N/A'}
              </p>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border ${getConfidenceBg(confidenceScore)} text-center`}>
              <TrendingUp className={`w-6 h-6 mx-auto mb-2 ${getConfidenceColor(confidenceScore)}`} />
              <p className={`text-2xl font-bold ${getConfidenceColor(confidenceScore)}`}>
                {confidenceScore}%
              </p>
              <p className="text-xs text-slate-400">Confianza</p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 text-center">
              <Target className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <p className="text-lg font-bold text-white">
                {masterContext.maturity_level === 'Growing' ? 'Crecimiento' : 
                 masterContext.maturity_level === 'Early' ? 'Inicial' :
                 masterContext.maturity_level === 'Scaled' ? 'Escalado' :
                 masterContext.maturity_level === 'Enterprise' ? 'Empresa' :
                 masterContext.maturity_level}
              </p>
              <p className="text-xs text-slate-400">Madurez</p>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-lg font-bold text-white">
                {masterContext.is_valid ? 'Válido' : 'Revisar'}
              </p>
              <p className="text-xs text-slate-400">Estado</p>
            </div>
          </div>

          {/* Recommended KPIs */}
          {masterContext.recommended_kpis && masterContext.recommended_kpis.length > 0 && (
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-sm font-semibold text-white mb-3">KPIs Recomendados para tu Industria</h4>
              <div className="flex flex-wrap gap-2">
                {masterContext.recommended_kpis.map((kpi, idx) => (
                  <Badge key={idx} className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                    {kpi}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Modules */}
          {masterContext.recommended_modules && masterContext.recommended_modules.length > 0 && (
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <h4 className="text-sm font-semibold text-white mb-3">Módulos Recomendados</h4>
              <div className="flex flex-wrap gap-2">
                {masterContext.recommended_modules.map((module, idx) => (
                  <Badge key={idx} className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                    {module}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {masterContext.warnings && masterContext.warnings.length > 0 && (
            <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-yellow-300 mb-2">Advertencias</p>
                  {masterContext.warnings.map((warning, idx) => (
                    <p key={idx} className="text-sm text-yellow-200 mb-1">• {warning}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Reconfigure Button */}
          <Button
            onClick={onReconfigure}
            variant="outline"
            className="w-full"
          >
            <Building className="w-4 h-4 mr-2" />
            Actualizar Perfil de Empresa
          </Button>
        </CardContent>
      </Card>

      {/* Data Quality Card */}
      <Card className="glass-dark border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-sm">Calidad de Datos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Completitud</span>
            <Badge className="bg-slate-700 text-slate-300">
              {dataQuality.completeness || 'N/A'}
            </Badge>
          </div>
          {dataQuality.critical_data_present && dataQuality.critical_data_present.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 mb-2">Datos Críticos Presentes:</p>
              <div className="flex flex-wrap gap-1">
                {dataQuality.critical_data_present.map((data, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs text-green-400 border-green-500/30">
                    {data}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {dataQuality.missing_data && dataQuality.missing_data.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 mb-2">Datos Faltantes:</p>
              <div className="flex flex-wrap gap-1">
                {dataQuality.missing_data.map((data, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs text-red-400 border-red-500/30">
                    {data}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IndustryProfilePanel;
